// Marketing site content for EliteHire India. Single membership: ₹4,599 / 6 months.
export const PLAN = {
  name: "EliteHire Membership",
  price: "₹4,599",
  period: "6 months",
  tagline: "One membership. Full support until you're hired.",
  cta: "Register — ₹4,599 / 6 months",
  features: [
    "ATS-optimised, role-specific resume + cover letter",
    "LinkedIn profile optimisation",
    "Live mock interview sessions (HR & technical)",
    "200+ question bank with model answers",
    "Full access to the EliteHire training platform (courses, exams, voice drills)",
    "Direct referrals to 500+ hiring partners",
    "Application tracking, follow-ups & offer negotiation",
    "Dedicated mentor + post-placement support",
  ],
  guarantee: "If we don't place you within 90 days of completing your training, we extend your access — free.",
};

export const HOME = {
  hero: {
    headline: "Land Your Dream Job.",
    headlineAccent: "Guaranteed Support.",
    subheadline:
      "India's most trusted career launchpad for freshers and students. We build your resume, train you for interviews, and place you in your dream role — across every industry.",
  },
  trustBadges: ["1,000+ Students Placed", "All Industries", "Expert Mentors", "95% Success Rate"],
  stats: [
    { value: "1,000+", label: "Students Placed" },
    { value: "95%", label: "Success Rate" },
    { value: "500+", label: "Hiring Partners" },
    { value: "50+", label: "Industries Covered" },
  ],
  services: [
    {
      title: "Resume Building",
      tag: "Foundation",
      description:
        "A professionally crafted resume that gets you noticed. Our experts tailor your resume to the role and industry, highlighting your strengths even with zero experience.",
    },
    {
      title: "Interview Coaching",
      tag: "Preparation",
      description:
        "Walk into every interview with confidence. Our coaches run mock interviews, sharpen your communication, and prepare you for the toughest HR and technical rounds.",
    },
    {
      title: "Job Placement",
      tag: "Outcome",
      description:
        "We connect you directly with 500+ hiring partners across India. Our placement team actively advocates for you until you receive and accept an offer.",
    },
  ],
  howItWorks: {
    heading: "From Registration to Offer Letter",
    subheading: "A simple, proven 3-step process that has helped 1,000+ freshers land their first job.",
    steps: [
      { number: "01", title: "Register", description: "Join with the EliteHire Membership (₹4,599 / 6 months). Complete your profile and tell us about your background, skills, and target roles." },
      { number: "02", title: "Get Trained", description: "Our experts build your resume, run mock interviews, and prepare you with the skills and confidence to impress any recruiter." },
      { number: "03", title: "Get Hired", description: "We connect you with our network of 500+ hiring partners and actively advocate for you until you land your dream offer." },
    ],
  },
  testimonials: {
    heading: "Real Students. Real Offers.",
    subheading: "Hear from freshers who landed their dream jobs through EliteHire India.",
    items: [
      { name: "Arjun Sharma", role: "Software Engineer", company: "Infosys", location: "Bengaluru", package: "₹4.5 LPA", quote: "I had zero work experience and was getting rejected at every stage. EliteHire completely rebuilt my resume and the mock interviews were brutal in the best way. Offer letter in 28 days." },
      { name: "Priya Nair", role: "HR Executive", company: "HDFC Bank", location: "Mumbai", package: "₹3.8 LPA", quote: "My coach identified the exact moments where I was losing interviewers — filler words, vague answers, nervous energy. Three mock sessions later, I cleared all three rounds in my first attempt." },
      { name: "Rahul Verma", role: "Business Analyst", company: "Wipro", location: "Hyderabad", package: "₹5.2 LPA", quote: "EliteHire's placement team referred me directly to Wipro's HR. I didn't even apply through the portal — they called me. I'm now earning more than most of my engineering batchmates." },
    ],
  },
  finalCta: {
    heading: "Start Your Career Journey Today",
    subheading: "Join 1,000+ freshers who trusted EliteHire India to launch their careers. One registration. Unlimited support. Your dream job.",
  },
};

export const SERVICES = {
  hero: {
    eyebrow: "What we offer",
    headline: "Everything You Need to Land Your First Job",
    subheadline: "From a standout resume to your first offer letter — EliteHire India covers every step of your career journey with expert guidance and real placement support.",
  },
  services: [
    {
      number: "01", title: "Resume Building", tag: "Foundation",
      headline: "A Resume That Gets You Shortlisted",
      description: "Most freshers get rejected before the interview because their resume doesn't pass the ATS filter. Our experts craft a resume that works — even with zero work experience.",
      details: [
        { label: "ATS Optimisation", text: "Formatted and keyword-optimised to pass the automated screening systems used by 90% of companies." },
        { label: "Role-Specific Tailoring", text: "Tailored to the exact role and industry you're targeting — not a generic template." },
        { label: "Strengths Framing", text: "We highlight your academic projects, internships and skills so you stand out even without experience." },
        { label: "Cover Letter & LinkedIn", text: "A matching cover letter and a fully optimised LinkedIn profile to complete your presence." },
      ],
    },
    {
      number: "02", title: "Interview Coaching", tag: "Preparation",
      headline: "Walk In Confident. Walk Out With an Offer.",
      description: "Knowing the answers isn't enough — you need to deliver them under pressure. Our coaches simulate real interview conditions and give you the feedback that turns rejections into offers.",
      details: [
        { label: "Live Mock Interviews", text: "Sessions with experienced coaches who replicate the exact format of HR and technical rounds." },
        { label: "Personalised Feedback", text: "A detailed report after every session on your strengths, weaknesses and areas to improve." },
        { label: "Communication Training", text: "Body language, tone, pacing and confidence — the soft skills interviewers notice but rarely mention." },
        { label: "Question Bank", text: "200+ commonly asked HR and technical questions with model answers for your target role." },
      ],
    },
    {
      number: "03", title: "Job Placement", tag: "Outcome",
      headline: "We Don't Just Prepare You — We Place You.",
      description: "Our placement team actively advocates for you with 500+ hiring partners across India. We make calls, follow up, and push until you have an offer in hand.",
      details: [
        { label: "Direct Referrals", text: "We refer you directly to HR contacts at 500+ companies — bypassing the job portal queue." },
        { label: "Job Matching", text: "We match your profile, skills and location preferences to the right openings." },
        { label: "Active Follow-Up", text: "We track every application and follow up with hiring managers on your behalf." },
        { label: "Offer Support", text: "Once you get an offer, we help you evaluate it, negotiate and prepare for day one." },
      ],
    },
  ],
  faq: {
    heading: "Common Questions",
    items: [
      { question: "What does the membership include?", answer: "One ₹4,599 membership gives you 6 months of full support: resume building, interview coaching, the complete training platform (courses, exams, voice drills), and direct placement referrals." },
      { question: "Is there a placement guarantee?", answer: "If we don't place you within 90 days of completing your training, we extend your access for free until you're placed." },
      { question: "How long does the process take?", answer: "Most students receive their first offer within 30–60 days of registration, depending on your target role, location and how quickly you complete the training." },
      { question: "Do you work with all industries?", answer: "Yes. We have hiring partners across IT, banking, finance, retail, healthcare, logistics, manufacturing and more — 50+ industries across India." },
      { question: "What if I have no work experience?", answer: "That's exactly who we're built for. Our experts highlight academic projects, internships, certifications and skills in a way that makes freshers competitive." },
    ],
  },
};

export const SUCCESS = {
  hero: {
    eyebrow: "Student success stories",
    heading: "Real Students. Real Offers.",
    subheading: "Over 1,000 freshers have launched their careers through EliteHire India. Here are some of their stories.",
  },
  stats: [
    { value: "1,000+", label: "Students Placed" },
    { value: "94%", label: "Placement Rate" },
    { value: "₹4.2 LPA", label: "Average Package" },
    { value: "60 Days", label: "Avg. Time to Offer" },
  ],
  stories: [
    { name: "Arjun Sharma", role: "Software Engineer", company: "Infosys", location: "Bengaluru", package: "₹4.5 LPA", daysToOffer: 28, degree: "B.Tech CSE, JNTU Hyderabad (2023)", challenge: "8 months of rejections after graduation. No callbacks despite 200+ applications.", quote: "EliteHire completely rebuilt my resume — it went from a generic template to something that actually told my story. The mock interviews were brutal in the best way. By the time I walked into the Infosys interview, I felt like I had done it a hundred times before. Offer letter in 28 days.", highlight: true },
    { name: "Priya Nair", role: "HR Executive", company: "HDFC Bank", location: "Mumbai", package: "₹3.8 LPA", daysToOffer: 45, degree: "MBA HR, Symbiosis Pune (2024)", challenge: "Cleared written tests but kept failing HR rounds.", quote: "My coach identified the exact moments where I was losing interviewers — filler words, vague answers, nervous energy. Three mock sessions later, I cleared all three rounds at HDFC Bank in my first attempt." },
    { name: "Rahul Verma", role: "Business Analyst", company: "Wipro", location: "Hyderabad", package: "₹5.2 LPA", daysToOffer: 38, degree: "BBA, Osmania University (2023)", challenge: "Non-engineering background made it hard to break into IT.", quote: "Everyone told me BBA graduates don't get into IT. EliteHire matched me to Wipro's BA team and referred me directly to their HR. I didn't even apply through the portal — they called me." },
    { name: "Sneha Reddy", role: "Marketing Executive", company: "Swiggy", location: "Bengaluru", package: "₹4.0 LPA", daysToOffer: 52, degree: "B.Com, Bangalore University (2024)", challenge: "Commerce graduate with no idea how to enter the startup ecosystem.", quote: "EliteHire helped me reframe my college projects as real marketing experience and optimised my LinkedIn. Within a week I had three recruiters reaching out. Swiggy was one of them." },
    { name: "Karthik Iyer", role: "Data Analyst", company: "TCS", location: "Chennai", package: "₹3.6 LPA", daysToOffer: 41, degree: "B.Sc Statistics, Madras University (2023)", challenge: "Strong technical skills but couldn't present them to non-technical HR panels.", quote: "My coach taught me how to tell a story with data — not just list tools. TCS's HR panel specifically mentioned my communication as a standout. I got placed in the analytics division." },
    { name: "Divya Menon", role: "Operations Associate", company: "Amazon India", location: "Hyderabad", package: "₹4.8 LPA", daysToOffer: 33, degree: "B.Tech Industrial Engg, NIT Warangal (2024)", challenge: "Missed campus placements and felt behind peers.", quote: "EliteHire connected me to Amazon's off-campus drive and prepared me for their leadership-principle interviews. I got the offer in 33 days — a higher package than most of my on-campus friends." },
  ],
};

export const ABOUT = {
  hero: {
    eyebrow: "Our story",
    headline: "Built for Freshers. Driven by Results.",
    subheadline: "EliteHire India was founded with one belief: every graduate deserves a fair shot at their dream career — regardless of college tier, city, or connections.",
  },
  story: {
    heading: "Why We Started",
    paragraphs: [
      "Every year, millions of graduates enter India's job market with degrees in hand but no idea how to navigate it. They send hundreds of applications, get no responses, and slowly lose confidence — not because they lack talent, but because no one taught them how to present it.",
      "EliteHire India was built to fix that. We started as a small team of HR professionals and career coaches who had seen this problem from the inside — watching talented freshers get rejected for avoidable reasons. A poorly formatted resume. A nervous interview. No network to tap.",
      "Today, we've helped over 1,000 students across India land their first jobs. Our process is simple, our results are real, and our commitment to every student we work with is total.",
    ],
  },
  mission: {
    heading: "Our Mission",
    statement: "To make quality career support accessible to every Indian fresher — and to ensure that talent, not privilege, determines who gets hired.",
    values: [
      { title: "Results First", description: "We measure our success by your offer letter, not the number of sessions we run." },
      { title: "Honest Guidance", description: "We tell you what you need to hear, not what you want to hear." },
      { title: "Accessible Support", description: "Quality career support shouldn't cost a fortune — every fresher can afford the help they need." },
      { title: "Long-Term Relationships", description: "We don't disappear after you get hired. Post-placement support keeps you set up for growth." },
    ],
  },
  stats: [
    { value: "1,000+", label: "Students Placed" },
    { value: "95%", label: "Success Rate" },
    { value: "500+", label: "Hiring Partners" },
    { value: "50+", label: "Industries" },
    { value: "6+", label: "Years Experience" },
    { value: "28+", label: "Cities" },
  ],
  team: {
    heading: "The Team Behind Your Placement",
    subheading: "Our coaches and placement specialists have decades of combined experience in HR, recruitment and career development.",
    members: [
      { name: "Kalyan Naini", role: "Founder & CEO", bio: "Former SaaS specialist with 15 years at top MNCs. Kalyan built EliteHire after seeing thousands of talented freshers fail at the resume stage — and decided to do something about it." },
      { name: "Sai Ram Reddy", role: "Co-founder", bio: "Career strategist and placement expert with over a decade connecting freshers with top employers. Sai Ram drives partnerships with 500+ hiring companies." },
    ],
  },
};

export const CONTACT = {
  hero: {
    eyebrow: "Get in touch",
    headline: "We're Here to Help",
    subheadline: "Have a question about the membership, the placement process, or anything else? Reach out and our team will get back to you within 24 hours.",
  },
  details: [
    { label: "Email", value: "support@elitehireindia.com", note: "We reply within 24 hours" },
    { label: "Phone", value: "+91 9618351570", note: "Mon–Sat, 9 AM – 7 PM IST" },
    { label: "Founder & CEO", value: "Kalyan Naini", note: "kalyan@elitehireindia.com" },
  ],
  office: { address: "EliteHire India Pvt. Ltd.", city: "Hyderabad, Telangana — 500081", country: "India" },
  topics: ["General Enquiry", "EliteHire Membership — ₹4,599", "Placement Support", "Resume / Interview Coaching", "Partnership / Hiring"],
};
